import React, {
  useReducer,
  useCallback,
  useMemo,
  useRef,
  useEffect,
  useSyncExternalStore,
} from "react";
import {
  Box,
  Flex,
  Input,
  Spinner,
  Tag,
  Text,
} from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";

function debounce(fn, waitMs) {
  let timeoutId;
  const debounced = (...args) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => fn(...args), waitMs);
  };
  debounced.cancel = () => clearTimeout(timeoutId);
  return debounced;
}

const apiUrl = process.env.REACT_APP_API_URL;
const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || "/api";
const searchEndpoint = apiUrl
  ? `${apiUrl}/api/search`
  : `${apiBaseUrl.replace(/\/$/, "")}/search`;

const DEFAULT_INPUT_PROPS = {};
const DEFAULT_CONTAINER_PROPS = {};
const DEFAULT_SPINNER_PROPS = {};

const INITIAL_SEARCH_STATE = {
  value: "",
  suggestions: [],
  open: false,
  highlighted: -1,
};

function searchReducer(state, action) {
  switch (action.type) {
    case "SET_VALUE":
      if (!action.value.trim()) {
        return {
          ...state,
          value: action.value,
          suggestions: [],
          open: false,
          highlighted: -1,
        };
      }
      return { ...state, value: action.value };
    case "SET_SUGGESTIONS":
      return {
        ...state,
        suggestions: action.suggestions,
        highlighted: -1,
        open: action.suggestions.length > 0,
      };
    case "CLEAR_SUGGESTIONS":
      return {
        ...state,
        suggestions: [],
        open: false,
        highlighted: -1,
      };
    case "CLOSE":
      return { ...state, open: false };
    case "CLEAR_INPUT":
      return { ...INITIAL_SEARCH_STATE };
    case "SET_HIGHLIGHTED":
      return {
        ...state,
        highlighted:
          typeof action.highlighted === "function"
            ? action.highlighted(state.highlighted)
            : action.highlighted,
      };
    default:
      return state;
  }
}

const fetchSuggestions = async (value) => {
  if (!value?.trim()) return [];
  const res = await fetch(`${searchEndpoint}?q=${encodeURIComponent(value)}`);
  if (!res.ok) throw new Error("network");
  return res.json();
};

function useInFlight() {
  const inFlightRef = useRef(0);
  const listenersRef = useRef(new Set());

  const subscribe = useCallback((listener) => {
    listenersRef.current.add(listener);
    return () => listenersRef.current.delete(listener);
  }, []);

  const getSnapshot = useCallback(() => inFlightRef.current > 0, []);

  const isInFlight = useSyncExternalStore(subscribe, getSnapshot, () => false);

  const setInFlight = useCallback((active) => {
    const next = Math.max(0, inFlightRef.current + (active ? 1 : -1));
    if (next === inFlightRef.current) return;
    inFlightRef.current = next;
    listenersRef.current.forEach((listener) => listener());
  }, []);

  return { isInFlight, setInFlight };
}

const StockSearch = ({
  placeholder = "Search by ticker, company name, or CIK",
  inputProps: chakraInputProps = DEFAULT_INPUT_PROPS,
  containerProps = DEFAULT_CONTAINER_PROPS,
  spinnerProps = DEFAULT_SPINNER_PROPS,
  onSuggestionSelected,
  onSelectionComplete,
  navigateOnSelect = true,
  clearOnSelect = false,
}) => {
  const [{ value, suggestions, open, highlighted }, dispatch] = useReducer(
    searchReducer,
    INITIAL_SEARCH_STATE,
  );
  const { isInFlight, setInFlight } = useInFlight();
  const navigate = useNavigate();
  const containerRef = useRef(null);
  const listId = "stock-search-suggestions";

  const loadSuggestions = useCallback(
    async (val) => {
      if (!val?.trim()) {
        dispatch({ type: "CLEAR_SUGGESTIONS" });
        return;
      }
      try {
        setInFlight(true);
        const data = await fetchSuggestions(val);
        dispatch({ type: "SET_SUGGESTIONS", suggestions: data });
      } catch (err) {
        console.error("Failed to fetch stock suggestions", err);
        dispatch({ type: "CLEAR_SUGGESTIONS" });
      } finally {
        setInFlight(false);
      }
    },
    [setInFlight],
  );
  const debouncedLoad = useMemo(
    () => debounce(loadSuggestions, 300),
    [loadSuggestions],
  );

  useEffect(() => () => debouncedLoad.cancel(), [debouncedLoad]);

  useEffect(() => {
    const onDocMouseDown = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        dispatch({ type: "CLOSE" });
      }
    };
    document.addEventListener("mousedown", onDocMouseDown);
    return () => document.removeEventListener("mousedown", onDocMouseDown);
  }, []);

  const handleSelect = useCallback(
    async (suggestion) => {
      const isPreIpo = suggestion?.entity_type === 'pre_ipo' && suggestion?.cik;
      if (!suggestion?.ticker && !isPreIpo) return;
      dispatch({ type: clearOnSelect ? "CLEAR_INPUT" : "CLOSE" });
      try {
        if (onSuggestionSelected) {
          await onSuggestionSelected(suggestion);
        }
      } catch (err) {
        console.error("Post-selection handler failed", err);
      }
      if (navigateOnSelect) {
        navigate(
          isPreIpo
            ? suggestion.path || `/company/${suggestion.cik}/sec-filings`
            : `/stock/${suggestion.ticker.toLowerCase()}`
        );
      }
      onSelectionComplete?.();
    },
    [clearOnSelect, navigate, navigateOnSelect, onSuggestionSelected, onSelectionComplete],
  );

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    dispatch({ type: "SET_VALUE", value: newValue });
    if (!newValue.trim()) {
      debouncedLoad.cancel();
      return;
    }
    debouncedLoad(newValue);
  };

  const handleKeyDown = (e) => {
    if (!open || suggestions.length === 0) {
      if (e.key === "Escape") {
        dispatch({ type: "CLOSE" });
      }
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      dispatch({
        type: "SET_HIGHLIGHTED",
        highlighted: (prev) =>
          prev < suggestions.length - 1 ? prev + 1 : prev,
      });
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      dispatch({
        type: "SET_HIGHLIGHTED",
        highlighted: (prev) => (prev > 0 ? prev - 1 : 0),
      });
    } else if (e.key === "Enter") {
      if (highlighted >= 0 && highlighted < suggestions.length) {
        e.preventDefault();
        void handleSelect(suggestions[highlighted]);
      }
    } else if (e.key === "Escape") {
      e.preventDefault();
      dispatch({ type: "CLOSE" });
    }
  };

  const inputStyles = {
    size: "lg",
    bg: "white",
    borderRadius: "lg",
    boxShadow: "sm",
    pl: 5,
    pr: 10,
    ...chakraInputProps,
  };

  const boxProps = {
    w: "100%",
    position: "relative",
    ...containerProps,
  };

  return (
    <Box ref={containerRef} {...boxProps}>
      <Input
        {...inputStyles}
        placeholder={placeholder}
        value={value}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        autoComplete="off"
        role="combobox"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        aria-autocomplete="list"
        aria-activedescendant={
          open && highlighted >= 0
            ? `${listId}-option-${highlighted}`
            : undefined
        }
      />

      {open && suggestions.length > 0 && (
        <Box
          id={listId}
          role="listbox"
          position="absolute"
          top="calc(100% + 4px)"
          left={0}
          right={0}
          bg="white"
          borderWidth="1px"
          borderColor="gray.200"
          borderRadius="md"
          boxShadow="0 4px 12px rgba(0,0,0,0.08)"
          maxH="260px"
          overflowY="auto"
          zIndex={20}
        >
          {suggestions.map((suggestion, idx) => (
            <Box
              key={suggestion.cik ? `cik-${suggestion.cik}` : suggestion.ticker}
              id={`${listId}-option-${idx}`}
              role="option"
              aria-selected={highlighted === idx}
              cursor="pointer"
              bg={highlighted === idx ? "teal.50" : "transparent"}
              onMouseDown={(e) => {
                e.preventDefault();
                void handleSelect(suggestion);
              }}
              onMouseEnter={() =>
                dispatch({ type: "SET_HIGHLIGHTED", highlighted: idx })
              }
            >
              <Flex justify="space-between" px={4} py={2}>
                <Box>
                  <Flex align="center" gap={2}>
                    <Text fontWeight="medium">
                      {suggestion.proposed_ticker || suggestion.ticker || 'Company'}
                    </Text>
                    {suggestion.entity_type === 'pre_ipo' && (
                      <Tag.Root size="sm" colorPalette="teal" variant="subtle">
                        <Tag.Label>Pre-IPO</Tag.Label>
                      </Tag.Root>
                    )}
                    {suggestion.entity_type === 'archived' && (
                      <Tag.Root size="sm" colorPalette="gray" variant="subtle">
                        <Tag.Label>Archived</Tag.Label>
                      </Tag.Root>
                    )}
                  </Flex>
                  {suggestion.entity_type === 'pre_ipo' && (
                    <Text fontSize="xs" color="gray.500">CIK {suggestion.cik}</Text>
                  )}
                </Box>
                <Text fontSize="sm" color="gray.500" textAlign="right">
                  {suggestion.name}
                </Text>
              </Flex>
            </Box>
          ))}
        </Box>
      )}

      {isInFlight && (
        <Spinner
          size="sm"
          position="absolute"
          right={3}
          top="50%"
          transform="translateY(-50%)"
          {...spinnerProps}
        />
      )}
    </Box>
  );
};

export default StockSearch;
